import React, { useState, useEffect, useRef } from 'react';
import { addDoc, getDocs, getDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { auth, TASK_STATUS } from '../firebase.config.js';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAgency } from '../AgencyContext';
import { useTaskTracker } from '../TaskTrack/useTaskTracker';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import './ExecuteTask.css';

const TaskDropdown = ({ value, options, placeholder, onChange, autoOpen = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Auto-open dropdown after delay
  useEffect(() => {
    if (autoOpen && options.length > 0 && !hasAutoOpened && !value) {
      const timer = setTimeout(() => {
        setIsOpen(true);
        setHasAutoOpened(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [autoOpen, options.length, hasAutoOpened, value]);

  const sel = options.find(o => o.value === value);

  return (
    <div className="airbnb-dropdown" ref={ref}>
      <div className="dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <div className="label">{sel ? sel.label : placeholder}</div>
        <div className={`arrow ${isOpen ? 'open' : ''}`}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="dropdown-menu">
          {options.length === 0 ? (
            <div className="empty">No tasks</div>
          ) : options.map(o => (
            <div key={o.value}
                 className={`option ${value === o.value ? 'selected' : ''}`}
                 onClick={() => { onChange(o.value); setIsOpen(false); }}>
              <div className="opt-label">{o.label}</div>
              {value === o.value && <div className="check">✓</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ExecuteTask = ({ selectedModel }) => {
  const [selectedTask, setSelectedTask] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sliderAnimated, setSliderAnimated] = useState(false);
  const [user] = useAuthState(auth);
  const { getAgencyCollection, getAgencyDoc, currentAgency } = useAgency();
  const { trackEvent } = useTaskTracker();
  const sliderRef = useRef(null);

  const quotes = [
    <>What would you like to <span style={{color: '#007AFF'}}>create</span> today?</>,
    <>Turn your vision into <span style={{color: '#007AFF'}}>reality</span></>,
    <>Every great <span style={{color: '#007AFF'}}>idea</span> starts with a single task</>,
    <><span style={{color: '#007AFF'}}>Create</span> without limits, <span style={{color: '#007AFF'}}>scale</span> without boundaries</>,
    <>Your <span style={{color: '#007AFF'}}>next</span> breakthrough is one launch away</>,
    <>From concept to <span style={{color: '#007AFF'}}>completion</span> in seconds</>,
    <>Where <span style={{color: '#007AFF'}}>creativity</span> meets artificial intelligence</>,
    <>Build the <span style={{color: '#007AFF'}}>future</span>, one task at a time</>,
    <><span style={{color: '#007AFF'}}>Unleash</span> your creative potential</>,
    <>Transform thoughts into tangible <span style={{color: '#007AFF'}}>results</span></>,
    <>Innovation at the <span style={{color: '#007AFF'}}>speed</span> of thought</>,
    <>Create <span style={{color: '#007AFF'}}>more</span>, worry less</>,
    <>Your <span style={{color: '#007AFF'}}>ideas</span> deserve to see the world</>,
    <>Make it <span style={{color: '#007AFF'}}>happen</span> with a single click</>,
    <>Dream big, <span style={{color: '#007AFF'}}>execute</span> bigger</>
  ];

  const [currentQuote] = useState(() => {
    return quotes[Math.floor(Math.random() * quotes.length)];
  });


  // Slider animation effect
  useEffect(() => {
    if (selectedModel && !sliderAnimated) {
      const timer = setTimeout(() => {
        // Subtle animation: just animate the slider thumb visually
        if (sliderRef.current) {
          sliderRef.current.classList.add('thumb-wiggle');
          setTimeout(() => {
            if (sliderRef.current) {
              sliderRef.current.classList.remove('thumb-wiggle');
            }
            setSliderAnimated(true);
          }, 1200); // Animation duration
        }
      }, 1000); // Start after 1 second (0.5s after dropdown animation)
      
      return () => clearTimeout(timer);
    }
  }, [selectedModel, sliderAnimated, quantity]);

  useEffect(() => {
    if (selectedModel) fetchTasksForAI(selectedModel);
    else { setAvailableTasks([]); setSelectedTask(''); }
  }, [selectedModel]);

  const fetchTasksForAI = async aiId => {
    try {
      const col = getAgencyCollection('aitask');
      const q = query(col, where('aiModelId','==',aiId), where('status','==','active'));
      const snap = await getDocs(q);
      setAvailableTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch(e) { console.error(e); }
  };

  const sendTask = async () => {
    if (!selectedModel || !selectedTask || quantity < 1) return;
    setLoading(true);
    try {
      const taskDocRef = getAgencyDoc('aitask', selectedTask);
      const taskData = (await getDoc(taskDocRef)).data();
      
      // Get the AI model data to retrieve the profile photo
      const aiModelDocRef = getAgencyDoc('aimodel', selectedModel);
      const aiModelData = (await getDoc(aiModelDocRef)).data();
      
      const docRef = await addDoc(getAgencyCollection('taskbacklog'), {
        aiModelId: selectedModel,
        taskId: selectedTask,
        taskName: taskData.name,
        description: taskData.description || '',
        profileName: taskData.profileName || aiModelData?.name || '',
        profilePhotoUrl: aiModelData?.photoURL || taskData.profilePhotoUrl || '',
        quantity, 
        hasL2Process: taskData.hasL2Process,
        isVideo: taskData.isVideo || false,
        workflow: taskData.workflow,
        status: TASK_STATUS.NOT_STARTED_L1,
        statusChangedAt: serverTimestamp(),
        assignedTo: null, assignedAt: null, currentAssignee: null,
        createdAt: serverTimestamp(), createdBy: user.email,
        completedImages:0, rejectedImages:0, approvedImages:0,
        comments: [], images: []
      });
      
      // Track the task execution
      await trackEvent(docRef.id, taskData.name, 'executed', currentAgency?.userRole || 'Manager', quantity, null, taskData.isVideo, taskData.hasL2Process, taskData.profileName, user.email);
      setShowSuccess(true);
      setTimeout(() => { setShowSuccess(false); setSelectedTask(''); setQuantity(1); }, 3000);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const taskOptions = availableTasks.map(t => ({
    value: t.id, label: t.name, sublabel: t.workflow
  }));

  if (!selectedModel) return (
    <div className="execute-task">
      <div className="execute-task__empty">
        <h2>Select an AI profile to launch tasks</h2>
      </div>
    </div>
  );

  return (
    <div className="execute-task">
      <div className="execute-task__quote">
        <h1>{currentQuote}</h1>
      </div>
      
      <div className="execute-task__container">
        <div className="execute-task__form">
          <TaskDropdown
            value={selectedTask}
            options={taskOptions}
            placeholder="Select task"
            onChange={setSelectedTask}
            autoOpen={false}
          />
          
          <div className="quantity-control">
            <input
              ref={sliderRef}
              type="range" 
              min="1" 
              max="300"
              value={quantity}
              onChange={e => {
                setQuantity(+e.target.value);
                setSliderAnimated(true); // Stop animation if user interacts
              }}
              className="quantity-slider"
            />
            <div className="quantity-display">{quantity}</div>
          </div>
          
          <button
            className={`execute-button ${!selectedTask || loading ? 'disabled' : ''}`}
            onClick={sendTask}
            disabled={!selectedTask || loading}
          >
            <AutoAwesomeIcon className="execute-icon" />
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
      
      {/* Success Toast */}
      {showSuccess && (
        <div className="success-toast">
          Task launched successfully
        </div>
      )}
    </div>
  );
};

export default ExecuteTask;
